// MSSQL Engine Extras — in-buffer scanners that enrich the alias / CTE
// scope with table variables (`DECLARE @t TABLE(...)`), local temporary
// tables (`CREATE TABLE #tmp(...)` / `##global`), OPENJSON column lists
// (`OPENJSON(expr) WITH (col type 'path', ...) AS alias`), and the
// detection of MERGE/INSERT/UPDATE/DELETE OUTPUT target so the
// `inserted` / `deleted` pseudo-tables expose the correct column set.
//
// Pure module — no Monaco / electron imports. Operates on the raw source
// + statement range pair the orchestrator already produces.
//
// Scanning rules:
//   1. Whole-buffer scope for table variables / temp tables — they are
//      declared once per script and visible to every subsequent statement,
//      so we walk from offset 0 to the cursor (not just the enclosing
//      statement).
//   2. Statement-local scope for OPENJSON / MERGE target / OUTPUT target —
//      the WITH (...) and INTO target are tied to a single statement.
//   3. Tokens come from the shared `tokenize()` so identifier / keyword /
//      operator classification stays consistent with the rest of the
//      pipeline. `DECLARE`, `CREATE`, `TABLE`, `OPENJSON` are *not* in
//      `SQL_KEYWORDS`, so they tokenize as `ident`. `MERGE`, `INTO`,
//      `OUTPUT` *are* keywords. We accept both `kw` and `ident` token
//      types when scanning for SQL words, per the existing
//      `nextWordIs` convention in `pseudoTables.ts`.

import { tokenize, type Token } from '../context/tokenizer';
import { parseIdentifierToken } from '../resolver/identifierUtils';
import {
  findMatchingRparen,
} from '../resolver/relationParser';

export type MssqlSyntheticRelation = {
  /** Bare name as it appears in source — `@t`, `#tmp`, `##global`,
   *  or the OPENJSON `AS alias` for OPENJSON synthetic relations. */
  readonly name: string;
  readonly columns: readonly string[];
  /** Best-effort column data type; `unknown` when the type couldn't be parsed. */
  readonly columnTypes: ReadonlyMap<string, string>;
  readonly kind: 'table-variable' | 'temp-table' | 'openjson';
  /** Source-text range covering the declaration; used to filter OPENJSON
   *  aliases so they don't leak across statement boundaries. */
  readonly definedAt: { readonly start: number; readonly end: number };
};

export type MssqlMergeOrOutputTarget = {
  readonly schema?: string;
  readonly name: string;
  /** Statement kind that owns this target. `$action` only valid for `merge`. */
  readonly statementKind: 'merge' | 'insert' | 'update' | 'delete';
};

const ENGINE = 'mssql' as const;

export function scanMssqlSyntheticRelations(
  source: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _cursor: number,
): readonly MssqlSyntheticRelation[] {
  // Scan the whole buffer regardless of cursor position. Two reasons:
  //   1. OPENJSON aliases sit after the SELECT clause: when the user is
  //      typing `SELECT j.| FROM OPENJSON(...) AS j`, the cursor is BEFORE
  //      the OPENJSON declaration. Truncating at cursor would miss it.
  //   2. Table variables / temp tables declared above the cursor are
  //      already in scope; ones below are typed by the user and not yet
  //      complete, but tolerating them is harmless — they just don't
  //      appear in the binding list because the relation wouldn't parse.
  // Statement-level scoping for OPENJSON is enforced separately at the
  // resolver via `visibleSynthRels(rels, statementRange)`.
  const tokens = tokenize(source, 0, source.length, ENGINE);
  const out: MssqlSyntheticRelation[] = [];
  // User-defined TABLE types declared in this buffer, keyed by their bare
  // (lower-cased) name — a `@var` typed with one of them IS a table variable
  // (script.43: `@list dbo.acact_ProductList`), so the pass below binds it.
  const tableTypes = new Map<string, BufferTableType>(
    scanCreateTableTypes(tokens).map((t) => [t.name.toLowerCase(), t]),
  );
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    const upper = identUpper(tok);
    if (!upper) continue;

    if (upper === 'DECLARE') {
      const r = scanDeclareTableVariable(tokens, i);
      if (r) {
        out.push(r.relation);
        i = r.lastIdx;
      }
      continue;
    }
    // `RETURNS @t TABLE (…)` — a multi-statement table-valued function's
    // return variable. Same shape and same scope as a `DECLARE @t TABLE`
    // (the body INSERTs into it and SELECTs from it), but it is declared in
    // the routine SIGNATURE, so the DECLARE scan never saw it and
    // `INSERT INTO @Result ▮` / `@Result.▮` had nothing to offer
    // (script.39 §4).
    if (upper === 'RETURNS') {
      const r = scanDeclareTableVariable(tokens, i);
      if (r) {
        out.push(r.relation);
        i = r.lastIdx;
      }
      continue;
    }
    if (upper === 'CREATE') {
      const r = scanCreateTempTable(tokens, i);
      if (r) {
        out.push(r.relation);
        i = r.lastIdx;
      }
      continue;
    }
    if (upper === 'OPENJSON') {
      const r = scanOpenJsonWithClause(tokens, i);
      if (r) {
        out.push(r.relation);
        i = r.lastIdx;
      }
      continue;
    }
    if (tableTypes.size > 0 && tok!.type === 'ident' && tok!.text.startsWith('@')) {
      const r = scanTableTypedVariable(tokens, i, tableTypes);
      if (r) {
        out.push(r.relation);
        i = r.lastIdx;
      }
    }
  }
  return out;
}

export type MssqlBufferTableType = {
  readonly name: string;
  readonly schema: string | null;
  readonly columns: readonly string[];
  readonly columnTypes: ReadonlyMap<string, string>;
  /** Offset of the `CREATE` token — callers position-bound on it. */
  readonly declaredAt: number;
};

type BufferTableType = MssqlBufferTableType;

/**
 * User-defined TABLE types (`CREATE TYPE [schema.]name AS TABLE (…)`) declared
 * in this buffer BEFORE `cursorOffset`. The snapshot cannot know them until
 * the DDL runs, and a script that declares a TVP type and then uses it as a
 * parameter / variable type two statements later is the normal flow
 * (script.43 §4/§10).
 */
export function scanMssqlBufferTableTypes(
  source: string,
  cursorOffset: number,
): readonly MssqlBufferTableType[] {
  return scanCreateTableTypes(tokenize(source, 0, source.length, ENGINE)).filter(
    (t) => t.declaredAt < cursorOffset,
  );
}

/**
 * `CREATE TYPE [schema.]name AS TABLE ( col type, … )` — the T-SQL
 * table-valued-parameter type. Callers key by the bare name, lower-cased: a
 * declaration site writes `dbo.acact_ProductList`, and matching on the last
 * segment keeps the lookup schema-insensitive (a buffer that declares two
 * same-named types in different schemas is not worth modelling here).
 */
function scanCreateTableTypes(tokens: readonly Token[]): MssqlBufferTableType[] {
  const out: MssqlBufferTableType[] = [];
  for (let i = 0; i < tokens.length; i += 1) {
    if (identUpper(tokens[i]) !== 'CREATE' || identUpper(tokens[i + 1]) !== 'TYPE') continue;
    const nameWalk = readDottedName(tokens, i + 2);
    if (!nameWalk) continue;
    const { name, schema } = nameWalk;
    const j = nameWalk.nextIdx;
    if (identUpper(tokens[j]) !== 'AS' || identUpper(tokens[j + 1]) !== 'TABLE') continue;
    const lparenIdx = j + 2;
    if (tokens[lparenIdx]?.type !== 'lparen') continue;
    const rparenIdx = findMatchingRparen(tokens, lparenIdx);
    if (rparenIdx === -1) continue;
    const cols = parseColumnDefs(tokens, lparenIdx + 1, rparenIdx);
    out.push({
      name,
      schema,
      columns: cols.names,
      columnTypes: cols.types,
      declaredAt: tokens[i]!.start,
    });
    i = rparenIdx;
  }
  return out;
}

/**
 * `@var [schema.]<table type>` — a parameter or DECLARE whose type is one of
 * the buffer's TABLE types. Surfaced as a table-variable synthetic relation so
 * `FROM @var x` binds and `x.▮` / `@var.▮` complete to the type's columns
 * (script.43 §5/§6).
 */
function scanTableTypedVariable(
  tokens: readonly Token[],
  startIdx: number,
  tableTypes: ReadonlyMap<string, BufferTableType>,
): { relation: MssqlSyntheticRelation; lastIdx: number } | null {
  const typeWalk = readDottedName(tokens, startIdx + 1);
  if (!typeWalk) return null;
  const lastIdx = typeWalk.nextIdx - 1;
  const type = tableTypes.get(typeWalk.name.toLowerCase());
  if (!type) return null;
  return {
    relation: {
      name: tokens[startIdx]!.text,
      columns: type.columns,
      columnTypes: type.columnTypes,
      kind: 'table-variable',
      definedAt: { start: tokens[startIdx]!.start, end: tokens[lastIdx]!.end },
    },
    lastIdx,
  };
}

function scanDeclareTableVariable(
  tokens: readonly Token[],
  startIdx: number,
): { relation: MssqlSyntheticRelation; lastIdx: number } | null {
  // `DECLARE @t TABLE ( col1 type [, ...] )`
  const nameTok = tokens[startIdx + 1];
  if (!nameTok || nameTok.type !== 'ident') return null;
  if (!nameTok.text.startsWith('@')) return null;
  const tableTok = tokens[startIdx + 2];
  if (!tableTok || identUpper(tableTok) !== 'TABLE') return null;
  const lparenIdx = startIdx + 3;
  const lparenTok = tokens[lparenIdx];
  if (!lparenTok || lparenTok.type !== 'lparen') return null;
  const rparenIdx = findMatchingRparen(tokens, lparenIdx);
  if (rparenIdx === -1) return null;
  const cols = parseColumnDefs(tokens, lparenIdx + 1, rparenIdx);
  return {
    relation: {
      name: nameTok.text,
      columns: cols.names,
      columnTypes: cols.types,
      kind: 'table-variable',
      definedAt: { start: tokens[startIdx].start, end: tokens[rparenIdx].end },
    },
    lastIdx: rparenIdx,
  };
}

function scanCreateTempTable(
  tokens: readonly Token[],
  startIdx: number,
): { relation: MssqlSyntheticRelation; lastIdx: number } | null {
  // `CREATE TABLE #t (col1 type, ...)` or `##global` (double-#).
  const tableTok = tokens[startIdx + 1];
  if (!tableTok || identUpper(tableTok) !== 'TABLE') return null;
  // The name may be qualified (e.g. `tempdb..#tmp`) but `#tmp` /
  // `##global` are the typical forms. We walk the dotted-name to find an
  // identifier starting with `#`.
  let nameIdx = startIdx + 2;
  while (
    nameIdx < tokens.length &&
    (tokens[nameIdx].type === 'ident' || tokens[nameIdx].type === 'dot')
  ) {
    nameIdx += 1;
  }
  // Walk back from end of name run to last ident.
  let nameTokIdx = nameIdx - 1;
  while (nameTokIdx > startIdx + 1 && tokens[nameTokIdx].type !== 'ident') {
    nameTokIdx -= 1;
  }
  if (nameTokIdx <= startIdx + 1) return null;
  const nameTok = tokens[nameTokIdx];
  if (!nameTok.text.startsWith('#')) return null;
  // Next token must be `(`.
  const lparenIdx = nameTokIdx + 1;
  const lparenTok = tokens[lparenIdx];
  if (!lparenTok || lparenTok.type !== 'lparen') return null;
  const rparenIdx = findMatchingRparen(tokens, lparenIdx);
  if (rparenIdx === -1) return null;
  const cols = parseColumnDefs(tokens, lparenIdx + 1, rparenIdx);
  return {
    relation: {
      name: nameTok.text,
      columns: cols.names,
      columnTypes: cols.types,
      kind: 'temp-table',
      definedAt: { start: tokens[startIdx].start, end: tokens[rparenIdx].end },
    },
    lastIdx: rparenIdx,
  };
}

function scanOpenJsonWithClause(
  tokens: readonly Token[],
  startIdx: number,
): { relation: MssqlSyntheticRelation; lastIdx: number } | null {
  // `OPENJSON ( ... ) WITH ( col type [path] [, ...] ) [AS] alias`
  const argLparenIdx = startIdx + 1;
  const argLparen = tokens[argLparenIdx];
  if (!argLparen || argLparen.type !== 'lparen') return null;
  const argRparenIdx = findMatchingRparen(tokens, argLparenIdx);
  if (argRparenIdx === -1) return null;
  const withIdx = argRparenIdx + 1;
  const withTok = tokens[withIdx];
  if (!withTok || identUpper(withTok) !== 'WITH') return null;
  const withLparenIdx = withIdx + 1;
  const withLparen = tokens[withLparenIdx];
  if (!withLparen || withLparen.type !== 'lparen') return null;
  const withRparenIdx = findMatchingRparen(tokens, withLparenIdx);
  if (withRparenIdx === -1) return null;
  const cols = parseOpenJsonColumns(tokens, withLparenIdx + 1, withRparenIdx);
  // Optional `[AS] alias`.
  let aliasIdx = withRparenIdx + 1;
  let alias: string | null = null;
  if (aliasIdx < tokens.length) {
    const t = tokens[aliasIdx];
    if (identUpper(t) === 'AS') aliasIdx += 1;
  }
  if (aliasIdx < tokens.length && tokens[aliasIdx].type === 'ident') {
    alias = parseIdentifierToken(tokens[aliasIdx].text).content;
  }
  if (!alias) return null;
  return {
    relation: {
      name: alias,
      columns: cols.names,
      columnTypes: cols.types,
      kind: 'openjson',
      definedAt: { start: tokens[startIdx].start, end: tokens[aliasIdx].end },
    },
    lastIdx: aliasIdx,
  };
}

/**
 * Parse the column declarations inside `( col1 type, col2 type CONSTRAINT ... )`.
 * Stops a column at the next top-level `,` (paren-aware).
 */
function parseColumnDefs(
  tokens: readonly Token[],
  from: number,
  to: number,
): { names: readonly string[]; types: ReadonlyMap<string, string> } {
  const names: string[] = [];
  const types = new Map<string, string>();
  let i = from;
  while (i < to) {
    // Skip a top-level CONSTRAINT / PRIMARY KEY / UNIQUE / FOREIGN KEY /
    // CHECK / INDEX clause (these don't introduce a column).
    if (looksLikeTableConstraint(tokens, i)) {
      i = advanceToTopLevelComma(tokens, i, to);
      if (i < to) i += 1; // skip the comma
      continue;
    }
    const t = tokens[i];
    if (t && t.type === 'ident') {
      const colName = parseIdentifierToken(t.text).content;
      names.push(colName);
      // Capture the immediate next ident as the data type (best-effort).
      const typeTok = tokens[i + 1];
      if (typeTok && (typeTok.type === 'ident' || typeTok.type === 'kw')) {
        types.set(colName.toLowerCase(), typeTok.text);
      }
    }
    i = advanceToTopLevelComma(tokens, i, to);
    if (i < to) i += 1; // skip the comma
  }
  return { names, types };
}

function parseOpenJsonColumns(
  tokens: readonly Token[],
  from: number,
  to: number,
): { names: readonly string[]; types: ReadonlyMap<string, string> } {
  // OPENJSON columns syntax: `name type [N]'$.path' [AS JSON]` separated by `,`.
  // We can reuse parseColumnDefs (the type capture is the same shape; the
  // optional path literal sits past the type and the comma walker skips it).
  return parseColumnDefs(tokens, from, to);
}

function advanceToTopLevelComma(
  tokens: readonly Token[],
  from: number,
  to: number,
): number {
  let depth = 0;
  let i = from;
  while (i < to) {
    const t = tokens[i];
    if (t.type === 'lparen') depth += 1;
    else if (t.type === 'rparen') depth -= 1;
    else if (t.type === 'comma' && depth === 0) return i;
    i += 1;
  }
  return to;
}

function looksLikeTableConstraint(tokens: readonly Token[], idx: number): boolean {
  const upper = identUpper(tokens[idx]);
  if (!upper) return false;
  if (upper === 'CONSTRAINT' || upper === 'PRIMARY' || upper === 'UNIQUE' || upper === 'FOREIGN' || upper === 'CHECK' || upper === 'INDEX') {
    return true;
  }
  return false;
}

/**
 * Read a possibly-qualified object name (`name`, `schema.name`,
 * `db.schema.name`) starting at `startIdx`. Only a `.` continues the chain —
 * the next bare word after a complete name is the NEXT syntactic element
 * (`AS`, `READONLY`, an alias), never another segment.
 */
function readDottedName(
  tokens: readonly Token[],
  startIdx: number,
): { name: string; schema: string | null; nextIdx: number } | null {
  const first = tokens[startIdx];
  if (!first || (first.type !== 'ident' && first.type !== 'kw')) return null;
  let name = parseIdentifierToken(first.text).content;
  let schema: string | null = null;
  let j = startIdx + 1;
  while (tokens[j]?.type === 'dot') {
    const next = tokens[j + 1];
    if (!next || (next.type !== 'ident' && next.type !== 'kw')) break;
    schema = name;
    name = parseIdentifierToken(next.text).content;
    j += 2;
  }
  if (!name) return null;
  return { name, schema, nextIdx: j };
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}

/**
 * Find the target table of MERGE / INSERT / UPDATE / DELETE so the
 * `inserted` and `deleted` pseudo-table bindings can surface the
 * appropriate columns. Returns the first match scanning from the
 * statement start; returns null when no DML target is present (e.g. plain
 * SELECT).
 */
export function findOutputDmlTarget(
  source: string,
  statementStart: number,
  statementEnd: number,
): MssqlMergeOrOutputTarget | null {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  for (let i = 0; i < tokens.length; i += 1) {
    const upper = identUpper(tokens[i]);
    if (!upper) continue;
    if (upper === 'MERGE') {
      const j = upper === 'MERGE' && identUpper(tokens[i + 1]) === 'INTO' ? i + 2 : i + 1;
      const t = readQualifiedName(tokens, j, 'merge');
      if (t) return t.target;
      continue;
    }
    if (upper === 'INSERT') {
      // `INSERT TOP (n) INTO t` is legal T-SQL, so the row limiter is skipped
      // before the INTO check.
      const afterTop = skipTopClause(tokens, i + 1);
      if (identUpper(tokens[afterTop]) !== 'INTO') continue;
      const t = readQualifiedName(tokens, afterTop + 1, 'insert');
      if (t) return t.target;
      continue;
    }
    if (upper === 'UPDATE') {
      const t = readQualifiedName(tokens, skipTopClause(tokens, i + 1), 'update');
      if (t) return t.target;
      continue;
    }
    if (upper === 'DELETE') {
      const afterTop = skipTopClause(tokens, i + 1);
      const j = identUpper(tokens[afterTop]) === 'FROM' ? afterTop + 1 : afterTop;
      const t = readQualifiedName(tokens, j, 'delete');
      if (t) return t.target;
      continue;
    }
  }
  return null;
}

/**
 * Step over a T-SQL row limiter between the DML verb and its target —
 * `TOP (3)`, `TOP 3`, `TOP (3) PERCENT`. Returns `startIdx` unchanged when
 * there is none.
 *
 * Without this, `UPDATE TOP (3) Production.Product … OUTPUT inserted.▮` read
 * `TOP` as the target relation, so the pseudo-table resolved to a table that
 * does not exist and the OUTPUT list completed to nothing (script.50 §1-§4).
 */
function skipTopClause(tokens: readonly Token[], startIdx: number): number {
  if (identUpper(tokens[startIdx]) !== 'TOP') return startIdx;
  let i = startIdx + 1;
  if (tokens[i]?.type === 'lparen') {
    const close = findMatchingRparen(tokens, i);
    if (close === -1) return startIdx;
    i = close + 1;
  } else if (tokens[i]?.type === 'literal' || tokens[i]?.type === 'ident') {
    // `TOP 3` / `TOP @n` — a single unparenthesised operand.
    i += 1;
  } else {
    return startIdx;
  }
  if (identUpper(tokens[i]) === 'PERCENT') i += 1;
  return i;
}

function readQualifiedName(
  tokens: readonly Token[],
  startIdx: number,
  statementKind: MssqlMergeOrOutputTarget['statementKind'],
): { target: MssqlMergeOrOutputTarget; lastIdx: number } | null {
  if (startIdx >= tokens.length) return null;
  const head = tokens[startIdx];
  if (!head || head.type !== 'ident') return null;
  const parts: string[] = [parseIdentifierToken(head.text).content];
  let i = startIdx + 1;
  while (i < tokens.length && tokens[i].type === 'dot') {
    const next = tokens[i + 1];
    if (!next || (next.type !== 'ident' && next.type !== 'kw')) break;
    parts.push(parseIdentifierToken(next.text).content);
    i += 2;
  }
  const name = parts[parts.length - 1]!;
  const schema = parts.length >= 2 ? parts[parts.length - 2] : undefined;
  return {
    target: schema ? { schema, name, statementKind } : { name, statementKind },
    lastIdx: i - 1,
  };
}

/**
 * Tier-8 demotion check for MSSQL system catalogs. The `sys` schema
 * contains DMVs (`dm_exec_*`, `dm_os_*`, etc.), system views
 * (`tables`, `columns`, `objects`), and function-shaped catalogs.
 * `INFORMATION_SCHEMA` is the ANSI-shaped catalog. Both belong at the
 * bottom of the suggestion list — useful for power users, noisy for
 * regular completion.
 */
export function isMssqlSystemCatalog(schema: string | undefined): boolean {
  if (!schema) return false;
  const lower = schema.toLowerCase();
  return lower === 'sys' || lower === 'information_schema';
}

/** Keywords that close the OUTPUT column list when met at depth 0 on the
 *  backward walk — anything the cursor can sit after that is NOT the OUTPUT
 *  list itself (`OUTPUT … INTO @tbl` puts the target after INTO, `VALUES`
 *  starts the row list, …). */
const OUTPUT_LIST_STOP_KEYWORDS = new Set([
  'VALUES', 'SELECT', 'INTO', 'SET', 'WHERE', 'FROM', 'WHEN', 'ON', 'MERGE',
  'EXEC', 'DECLARE',
]);

/**
 * T19 — true when the cursor sits inside an `OUTPUT <list>` (before any
 * `INTO` / `VALUES` continuation). Backward depth-0 token walk: the first
 * stop keyword decides. OUTPUT classifies under the values arm (it is NOT
 * a `returning` clause — G12), so the resolver gates its INSERT-OUTPUT
 * emission on this.
 */
export function isInsideOutputList(
  source: string,
  cursorOffset: number,
  statementStart: number,
  statementEnd: number,
): boolean {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  let depth = 0;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i];
    if (t.start >= cursorOffset) continue;
    if (t.type === 'semicolon') return false;
    if (t.type === 'rparen') { depth += 1; continue; }
    if (t.type === 'lparen') { depth -= 1; continue; }
    if (depth !== 0) continue;
    const u = identUpper(t);
    if (u === 'OUTPUT') return true;
    if (u !== null && OUTPUT_LIST_STOP_KEYWORDS.has(u)) return false;
  }
  return false;
}
